import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, Form, Upload, Button, message, Alert } from 'antd';
// 补充配套图标
import { InboxOutlined, ArrowLeftOutlined, UploadOutlined, WarningOutlined } from '@ant-design/icons';
import { uploadImage } from '@/api/panelImages';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import type { RcFile, UploadFile, UploadChangeParam } from 'antd/es/upload/interface';

export const ImageUpload = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const panelId = searchParams.get('panelId');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!panelId) {
      message.error('无效的面板 ID');
      navigate('/panels');
    }
  }, [panelId, navigate]);

  if (!panelId) {
    return <LoadingSpinner />;
  }

  // 文件上传前验证
  const beforeUpload = (file: RcFile) => {
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      message.error('图片大小不能超过 5MB');
      return Upload.LIST_IGNORE;
    }

    const fileName = file.name.toLowerCase();
    const validExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif'];
    const isValidType = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isValidType) {
      message.error('仅支持 PNG、JPG、JPEG、WebP、GIF 格式的图片');
      return Upload.LIST_IGNORE;
    }

    setSelectedFile(file);
    return false;
  };

  // 处理文件变化
  const handleChange = (info: UploadChangeParam<UploadFile>) => {
    const latestFile = info.fileList[info.fileList.length - 1];
    setFileList(latestFile ? [latestFile] : []);
    
    if (info.file.status === 'removed') {
      setSelectedFile(null);
    }
  };

  const onFinish = async () => {
    if (!selectedFile) {
      message.error('请选择要上传的图片');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const result = await uploadImage(panelId, selectedFile);
      
      message.success(`图片上传成功！最新版本: v${result.version}`);
      
      queryClient.invalidateQueries({ queryKey: ['panel-images', panelId] });
      queryClient.invalidateQueries({ queryKey: ['panel', panelId] });
      
      navigate(`/panels/${panelId}/images`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '上传失败，请检查文件或网络。';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(`/panels/${panelId}/images`);
  };

  return (
    <div className="w-full space-y-8">
      {/* 标题+返回区 - 统一层级，适配小屏 */}
      <div className="flex flex-wrap justify-between items-center gap-4 max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <UploadOutlined className="text-blue-500" />
            上传面板图片
          </h1>
          <p className="text-gray-500 mt-1">
            为面板 ID: <span className="font-mono text-blue-600">{panelId}</span> 上传资源图片
          </p>
        </div>
        <Button 
          onClick={handleBack}
          icon={<ArrowLeftOutlined />}
          className="hover:bg-gray-50 transition-colors"
        >
          返回图片列表
        </Button>
      </div>
      
      {error && (
        <ErrorAlert 
          message="上传失败" 
          description={error} 
          className="mb-0 max-w-2xl"
          showIcon
        />
      )}

      {/* 上传表单卡片 - 统一无边框样式 */}
      <Card 
        title="上传新图片资源"
        className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 max-w-2xl"
        style={{ 
          padding: '24px',
          '--ant-card-head-font-size': '18px',
          '--ant-card-head-font-weight': 600,
          '--ant-card-head-color': '#1D2129'
        } as React.CSSProperties}
      >
        <Alert
          message="上传规则说明"
          description={
            <div className="space-y-1 text-gray-700">
              <p>• 支持格式：PNG、JPG、JPEG、WebP、GIF（推荐使用WebP，体积更小。）</p>
              <p>• 大小限制：单张图片最大 <strong className="text-red-600">5MB</strong></p>
              <p>• <strong className="text-orange-600">同名覆盖规则</strong>：上传与已有文件同名的图片，会自动覆盖旧版本并将版本号+1。</p>
              <p>• 覆盖后旧版本图片将被<strong className="text-red-600">永久删除</strong>，无法恢复。</p>
              <p>• 上传成功后系统会自动刷新图片列表，展示最新版本。</p>
            </div>
          }
          type="info"
          showIcon
          className="mb-8 rounded-lg"
        />

        <br />

        <Form 
          layout="vertical" 
          onFinish={onFinish} 
          className="space-y-8"
          requiredMark={false}
        >
          <Form.Item
            label="选择图片文件"
            rules={[{ required: true, message: '请选择要上传的图片文件' }]}
            validateStatus={selectedFile ? 'success' : (submitting ? 'validating' : 'error')}
            help={selectedFile 
              ? <span className="text-green-500 flex items-center mt-1">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  {selectedFile.name} · {Math.round(selectedFile.size / 1024)} KB
                </span>
              : <span className="text-red-500 flex items-center mt-1">
                  <WarningOutlined className="mr-1" />
                  请选择PNG/JPG/JPEG/WebP/GIF格式的图片（最大5MB）
                </span>
            }
            style={{ marginBottom: 0 }}
          >
            <Upload.Dragger
              accept=".png,.jpg,.jpeg,.webp,.gif"
              multiple={false}
              fileList={fileList}
              beforeUpload={beforeUpload}
              onChange={handleChange}
              onRemove={() => setSelectedFile(null)}
              disabled={submitting}
              showUploadList={false}
              className="rounded-lg"
              style={{ 
                border: selectedFile ? '2px solid #52c41a' : '2px dashed #d9d9d9',
                background: selectedFile ? '#f0f9eb' : 'transparent',
                padding: '24px',
                transition: 'all 0.3s'
              } as React.CSSProperties}
            >
              {selectedFile ? (
                <div className="flex flex-col items-center p-8 text-center">
                  <div className="text-6xl mb-4">🖼️</div>
                  <div className="font-medium text-lg mb-2 break-all max-w-full text-gray-800">
                    {selectedFile.name}
                  </div>
                  <div className="text-gray-600 text-sm mb-3">
                    {Math.round(selectedFile.size / 1024)} KB · {selectedFile.type.split('/')[1]?.toUpperCase()}
                  </div>
                  <div className="text-xs text-gray-500">
                    点击或拖拽其他图片可替换当前文件
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-5xl text-blue-500 mb-4">
                    <InboxOutlined />
                  </div>
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    点击或拖拽图片到此区域上传
                  </p>
                  <p className="text-gray-500 text-sm">
                    支持 PNG、JPG、JPEG、WebP、GIF 格式 · 最大 5MB
                  </p>
                </div>
              )}
            </Upload.Dragger>
          </Form.Item>

          <br />

          <Form.Item className="mb-0">
            <div className="flex space-x-4">
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitting}
                disabled={submitting || !selectedFile}
                className="flex-1 hover:shadow-md transition-shadow"
                style={{ borderRadius: '8px', fontSize: '16px' } as React.CSSProperties}
              >
                {submitting ? '上传中...' : '确认上传'}
              </Button>
              <Button 
                size="large" 
                onClick={handleBack} 
                disabled={submitting}
                style={{ borderRadius: '8px' } as React.CSSProperties}
              >
                取消
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};